"use client";

import React, { useEffect, useRef, useState } from "react";
import AppLogo from "@/components/ui/AppLogo";
import Link from "next/link";



// ─── Floating UI Card: Daily Brief Preview ───
const ElevateBriefCard: React.FC = () => (
  <div
    className="float-a glass-card rounded-2xl p-4 w-64 shadow-glass"
    style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)" }}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-amber-400" style={{ animation: "dot-blink 2s ease-in-out infinite" }} />
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#F59E0B", fontSize: "10px" }}>
          Elevate Brief
        </span>
      </div>
      <span className="text-xs" style={{ color: "#5C5A56" }}>6:00 AM</span>
    </div>
    <p className="text-sm font-semibold mb-2" style={{ color: "#F0EDE8", lineHeight: "1.4" }}>
      Good morning, Marcus.
    </p>
    <div className="space-y-1.5">
      {[
        { label: "Board call prep", status: "Ready", color: "#22C55E" },
        { label: "3 replies drafted", status: "Ghostwriter", color: "#F59E0B" },
        { label: "Airlock blocked 7 slots", status: "Protected", color: "#818CF8" },
      ].map((item, i) => (
        <div key={i} className="flex items-center justify-between">
          <span className="text-xs" style={{ color: "#9B9891" }}>{item.label}</span>
          <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: `${item.color}18`, color: item.color, fontSize: "10px" }}>
            {item.status}
          </span>
        </div>
      ))}
    </div>
    <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="flex items-center gap-1.5">
        <span className="text-xs" style={{ color: "#5C5A56" }}>Stress index:</span>
        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
          <div className="h-full rounded-full" style={{ width: "32%", background: "linear-gradient(90deg, #22C55E, #F59E0B)" }} />
        </div>
        <span className="text-xs font-medium" style={{ color: "#22C55E" }}>Low</span>
      </div>
    </div>
  </div>
);

// ─── Floating UI Card: Airlock Scheduler ───
const AirlockCard: React.FC = () => (
  <div
    className="float-b glass-card rounded-2xl p-4 w-56 shadow-glass"
    style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)" }}
  >
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "rgba(245,158,11,0.15)" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M8 2v3M16 2v3M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
      <span className="text-xs font-semibold" style={{ color: "#F0EDE8" }}>Airlock Engine</span>
    </div>
    <div className="space-y-1">
      {["9–10 AM", "11–12 PM", "2–3 PM"].map((time, i) => (
        <div key={i} className="flex items-center justify-between py-1 px-2 rounded-lg" style={{ background: i === 1 ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.03)" }}>
          <span className="text-xs" style={{ color: i === 1 ? "#F59E0B" : "#9B9891" }}>{time}</span>
          <span className="text-xs font-medium" style={{ color: i === 1 ? "#F59E0B" : "#5C5A56", fontSize: "10px" }}>
            {i === 1 ? "LOCKED" : "Free"}
          </span>
        </div>
      ))}
    </div>
    <div className="mt-3 text-center">
      <span className="text-xs" style={{ color: "#5C5A56" }}>Zero-click booking</span>
    </div>
  </div>
);

// ─── Floating UI Card: Ghostwriter ───
const GhostwriterCard: React.FC = () => {
  const [typed, setTyped] = useState("");
  const full = "Following up on the Q1 targets — I've reviewed the deck and I'm aligned. Let's connect Thursday.";

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (i < full.length) {
        setTyped(full.slice(0, i + 1));
        i++;
      } else {
        clearInterval(interval);
      }
    }, 28);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="float-c glass-card rounded-2xl p-4 w-60 shadow-glass"
      style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)" }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "rgba(129,140,248,0.15)" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="#818CF8" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <span className="text-xs font-semibold" style={{ color: "#F0EDE8" }}>Ghostwriter</span>
        <span className="ml-auto text-xs px-1.5 py-0.5 rounded" style={{ background: "rgba(129,140,248,0.15)", color: "#818CF8", fontSize: "10px" }}>AI</span>
      </div>
      <div className="rounded-lg p-2.5 text-xs leading-relaxed min-h-16" style={{ background: "rgba(255,255,255,0.03)", color: "#9B9891" }}>
        {typed}
        <span className="inline-block w-0.5 h-3 ml-0.5 align-middle" style={{ background: "#F59E0B", animation: "dot-blink 1s ease-in-out infinite" }} />
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <span className="text-xs" style={{ color: "#5C5A56", fontSize: "10px" }}>Tone: Executive · Relationship context loaded</span>
      </div>
    </div>
  );
};

// ─── Ambient Heatmap Orb ───
const AmbientOrb: React.FC = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Main amber orb */}
    <div
      className="orb-outer absolute rounded-full"
      style={{
        width: "600px",
        height: "600px",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -60%)",
        background: "radial-gradient(ellipse at center, rgba(245,158,11,0.18) 0%, rgba(245,158,11,0.06) 40%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    {/* Inner hot core */}
    <div
      className="orb-inner absolute rounded-full"
      style={{
        width: "200px",
        height: "200px",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -70%)",
        background: "radial-gradient(ellipse at center, rgba(239,68,68,0.12) 0%, rgba(245,158,11,0.08) 50%, transparent 80%)",
        filter: "blur(30px)",
      }}
    />
    {/* Grid overlay */}
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }}
    />
  </div>
);

// ─── Main Hero Section ───
const HeroSection: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden" style={{ background: "#0D0F14" }}>
      <AmbientOrb />

      {/* ─── NAVBAR ─── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrolled ? "rgba(13,15,20,0.9)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
          padding: scrolled ? "14px 0" : "22px 0",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <AppLogo size={36} />

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8">
            {["Features", "How It Works", "Pricing", "Testimonials"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium transition-colors duration-200"
                style={{ color: "#9B9891" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#F0EDE8")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#9B9891")}
              >
                {item}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link
              href="/login"
              className="hidden md:block text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-200"
              style={{ color: "#9B9891" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#F0EDE8")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#9B9891")}
            >
              Sign In
            </Link>
            <a
              href="#pricing"
              className="text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-300 glow-cta"
              style={{
                background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                color: "#0D0F14",
              }}
            >
              Join Waitlist
            </a>
            {/* Mobile menu toggle */}
            <button
              className="md:hidden flex flex-col gap-1.5 p-2"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span className="w-5 h-0.5 rounded-full transition-all" style={{ background: "#9B9891", transform: menuOpen ? "rotate(45deg) translate(2px, 6px)" : "none" }} />
              <span className="w-5 h-0.5 rounded-full transition-all" style={{ background: "#9B9891", opacity: menuOpen ? 0 : 1 }} />
              <span className="w-5 h-0.5 rounded-full transition-all" style={{ background: "#9B9891", transform: menuOpen ? "rotate(-45deg) translate(2px, -6px)" : "none" }} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden px-6 pt-4 pb-6 space-y-3" style={{ background: "rgba(13,15,20,0.97)", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            {["Features", "How It Works", "Pricing", "Testimonials"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/\s+/g, "-")}`}
                className="block text-sm font-medium py-2"
                style={{ color: "#9B9891" }}
                onClick={() => setMenuOpen(false)}
              >
                {item}
              </a>
            ))}
            <Link
              href="/login"
              className="block text-sm font-medium py-2"
              style={{ color: "#9B9891" }}
              onClick={() => setMenuOpen(false)}
            >
              Sign In
            </Link>
          </div>
        )}
      </nav>

      {/* ─── HERO CONTENT ─── */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center pt-28 pb-20 px-6">
        {/* Launch badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-xs font-semibold tracking-widest uppercase"
          style={{
            background: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.2)",
            color: "#F59E0B",
          }}
        >
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#F59E0B", animation: "dot-blink 2s ease-in-out infinite" }} />
          Launching March 2026 · Limited Early Access
        </div>

        {/* Headline */}
        <h1
          className="font-display text-center leading-none mb-6 max-w-4xl"
          style={{ fontSize: "clamp(52px, 8vw, 100px)", letterSpacing: "-0.03em" }}
        >
          <span style={{ color: "#F0EDE8", display: "block" }}>Your inbox,</span>
          <span
            className="font-display italic"
            style={{
              display: "block",
              background: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 50%, #EF4444 100%)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: "heatmap-shimmer 5s linear infinite",
            }}
          >
            finally tamed.
          </span>
        </h1>

        {/* Sub-headline */}
        <p
          className="text-center max-w-xl mb-10 leading-relaxed"
          style={{ color: "#9B9891", fontSize: "clamp(16px, 2vw, 20px)" }}
        >
          ElevateEmail's AI Triple-Threat handles your meetings, drafts your replies, and briefs you every morning — so you reclaim{" "}
          <span style={{ color: "#F0EDE8", fontWeight: 500 }}>3+ hours daily</span> without lifting a finger.
        </p>

        {/* CTA row */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
          <a
            href="#pricing"
            className="group flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-base transition-all duration-300 glow-cta"
            style={{
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              color: "#0D0F14",
            }}
          >
            Get Early Access — Free
            <svg className="transition-transform duration-300 group-hover:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </a>
          <a
            href="#features"
            className="flex items-center gap-2 px-8 py-4 rounded-2xl font-medium text-base transition-all duration-300"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#9B9891",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.08)";
              (e.currentTarget as HTMLElement).style.color = "#F0EDE8";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)";
              (e.currentTarget as HTMLElement).style.color = "#9B9891";
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 8l6 4-6 4V8z" fill="currentColor"/>
            </svg>
            Watch 90-sec demo
          </a>
          <a
            href="/dashboard"
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-all duration-300"
            style={{
              background: "rgba(245,158,11,0.08)",
              border: "1px solid rgba(245,158,11,0.2)",
              color: "#F59E0B",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "rgba(245,158,11,0.14)";
              (e.currentTarget as HTMLElement).style.borderColor = "rgba(245,158,11,0.4)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "rgba(245,158,11,0.08)";
              (e.currentTarget as HTMLElement).style.borderColor = "rgba(245,158,11,0.2)";
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
              <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
              <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
            </svg>
            View Dashboard
          </a>
        </div>

        <p className="text-sm" style={{ color: "#5C5A56" }}>
          No credit card · Gmail &amp; Outlook · Mobile-first
        </p>

        {/* ─── FLOATING CARDS ─── */}
        <div className="relative w-full max-w-4xl mt-16 hidden lg:block" style={{ height: "320px" }}>
          {/* Left card */}
          <div className="absolute" style={{ left: "0px", top: "40px" }}>
            <ElevateBriefCard />
          </div>
          {/* Center mockup */}
          <div
            className="absolute left-1/2 top-0 rounded-3xl overflow-hidden"
            style={{
              transform: "translateX(-50%)",
              width: "260px",
              height: "300px",
              background: "linear-gradient(145deg, #1A1D26, #13151C)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 60px rgba(245,158,11,0.08)",
            }}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#F59E0B", fontSize: "10px" }}>ElevateEmail</span>
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: i === 0 ? "#22C55E" : "rgba(255,255,255,0.15)" }} />
                  ))}
                </div>
              </div>
              {/* Fake email threads */}
              {[
                { from: "Sarah K.", subject: "Q1 Board Pack", time: "8:42", triaged: true },
                { from: "Raj Mehta", subject: "Partnership deck", time: "8:15", triaged: false },
                { from: "Team Atlas", subject: "Sprint review", time: "7:58", triaged: true },
                { from: "Investor Update", subject: "Monthly metrics", time: "7:30", triaged: true },
              ].map((email, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 py-2 rounded-lg px-2 mb-1"
                  style={{ background: i === 0 ? "rgba(245,158,11,0.06)" : "transparent" }}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                    style={{ background: `hsl(${i * 60 + 200}, 60%, 30%)`, color: `hsl(${i * 60 + 200}, 80%, 80%)` }}
                  >
                    {email.from[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium truncate" style={{ color: "#F0EDE8" }}>{email.from}</span>
                      <span className="text-xs ml-2 shrink-0" style={{ color: "#5C5A56", fontSize: "10px" }}>{email.time}</span>
                    </div>
                    <span className="text-xs truncate block" style={{ color: "#5C5A56" }}>{email.subject}</span>
                  </div>
                  {email.triaged && (
                    <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-2" style={{ background: "#F59E0B" }} />
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Right card */}
          <div className="absolute" style={{ right: "0px", top: "20px" }}>
            <AirlockCard />
          </div>
          {/* Bottom-center card */}
          <div className="absolute" style={{ left: "50%", bottom: "-20px", transform: "translateX(-50%) translateX(160px)" }}>
            <GhostwriterCard />
          </div>
        </div>

        {/* Mobile: simple stat row */}
        <div className="flex lg:hidden items-center gap-6 mt-10 flex-wrap justify-center">
          {[
            { value: "3+ hrs", label: "saved daily" },
            { value: "Zero", label: "clicks to book" },
            { value: "6 AM", label: "daily brief" },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl font-bold font-display" style={{ color: "#F59E0B" }}>{stat.value}</div>
              <div className="text-xs" style={{ color: "#5C5A56" }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-10"
        style={{ background: "linear-gradient(to bottom, transparent, #0D0F14)" }}
      />
    </section>
  );
};

export default HeroSection;